import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface HomeownerSubscriptionData {
  id: string;
  status: string;
  price_override_cents: number | null;
  stripe_connect_customer_id: string | null;
  stripe_connect_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  plan_name: string;
  plan_description: string | null;
  plan_price_cents: number;
  plan_billing_interval: string;
  capabilities: string[];
  provider_name: string | null;
}

/**
 * Fetch the active/pending service subscription for a property (homeowner view).
 * Returns the most recent non-canceled subscription with plan details, capabilities,
 * and service provider name.
 */
export function useHomeownerSubscription(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['homeowner-subscription', propertyId],
    queryFn: async (): Promise<HomeownerSubscriptionData | null> => {
      if (!propertyId) return null;

      // Get the most relevant subscription (active > trialing > pending > past_due)
      const { data: subs, error } = await supabase
        .from('property_service_subscriptions')
        .select(`
          id, status, price_override_cents,
          stripe_connect_customer_id, stripe_connect_subscription_id,
          current_period_start, current_period_end, cancel_at_period_end,
          service_plan_id
        `)
        .eq('property_id', propertyId)
        .in('status', ['active', 'trialing', 'pending', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      if (!subs || subs.length === 0) return null;

      const sub = subs[0] as Record<string, unknown>;
      const planId = sub.service_plan_id as string;

      // Fetch plan details
      const { data: plan, error: planError } = await supabase
        .from('service_plans')
        .select('name, description, price_cents, billing_interval, organization_id')
        .eq('id', planId)
        .single();

      if (planError) throw planError;

      // Fetch capabilities
      const { data: caps } = await supabase
        .from('service_plan_capabilities')
        .select('capability')
        .eq('plan_id', planId);

      // Fetch service provider org name
      let providerName: string | null = null;
      if (plan.organization_id) {
        const { data: org } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', plan.organization_id as string)
          .single();
        providerName = (org?.name as string) ?? null;
      }

      return {
        id: sub.id as string,
        status: sub.status as string,
        price_override_cents: sub.price_override_cents as number | null,
        stripe_connect_customer_id: sub.stripe_connect_customer_id as string | null,
        stripe_connect_subscription_id: sub.stripe_connect_subscription_id as string | null,
        current_period_start: sub.current_period_start as string | null,
        current_period_end: sub.current_period_end as string | null,
        cancel_at_period_end: sub.cancel_at_period_end as boolean,
        plan_name: plan.name as string,
        plan_description: plan.description as string | null,
        plan_price_cents: plan.price_cents as number,
        plan_billing_interval: plan.billing_interval as string,
        capabilities: ((caps ?? []) as Array<{ capability: string }>).map((c) => c.capability),
        provider_name: providerName,
      };
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
  });
}
